import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import Moment from 'moment'

import createFormHelpers from '../../../FormHelpers'
import AppointmentsService from '../../../services/AppointmentsService'
import CasesService from '../../../services/CasesService'
import TasksService from '../../../services/TasksService'
import validation from '../../../validation'
import Activity from '../../Activity'

export const isReportPrepActivity = activity => {
  if (!activity) return false
  const type = String(activity.type || '')
    .trim()
    .toLowerCase()
  if (type === 'report') return true
  const desc = String(activity.description || '').trim()
  return (
    /\b(?:progress\s+report|final\s+report|case\s+report|status\s+report|report)\s+(?:prep|preparation|writing|drafting|generation|compilation|synthesis|review)\b/i.test(
      desc,
    ) || /\b(?:prepar(?:e|ing|ation)\s+(?:of\s+)?(?:the\s+)?(?:progress\s+|final\s+|case\s+)?report)\b/i.test(desc)
  )
}

export const normalizeActivityDate = rawDate => {
  if (!rawDate) return Moment().format('YYYY-MM-DD')
  if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDate.trim())) {
    return rawDate.trim()
  }
  const m = Moment.utc(rawDate)
  if (m.isValid()) return m.format('YYYY-MM-DD')
  return Moment().format('YYYY-MM-DD')
}

export const findExistingReportPrepActivity = (reportModel, fallbackTasks = [], fallbackAppointments = []) => {
  const candidates = []

  const taskLookup = reportModel?.activityToTaskLookup || {}
  const apptLookup = reportModel?.activityToApptLookup || {}

  if (Array.isArray(reportModel?.activities)) {
    reportModel.activities.forEach(act => {
      if (isReportPrepActivity(act)) {
        const actNum = act.activityNumber
        candidates.push({
          activity: act,
          activityNumber: actNum,
          taskNumber: act.taskNumber || (actNum != null ? taskLookup[actNum] : null) || null,
          appointmentNumber: act.appointmentNumber || (actNum != null ? apptLookup[actNum] : null) || null,
        })
      }
    })
  }

  const scanTasks = tasksList => {
    if (!Array.isArray(tasksList)) return
    tasksList.forEach(task => {
      if (task?.onReportNumber) return
      const activities = Array.isArray(task?.activities) ? task.activities : []
      activities.forEach(act => {
        if (act?.onReportNumber) return
        if (isReportPrepActivity(act)) {
          const alreadyFound = candidates.some(
            c => c.activityNumber != null && act.activityNumber != null && c.activityNumber === act.activityNumber,
          )
          if (!alreadyFound) {
            candidates.push({
              activity: act,
              activityNumber: act.activityNumber,
              taskNumber: task.taskNumber || act.taskNumber || null,
              appointmentNumber: null,
            })
          } else {
            const match = candidates.find(c => c.activityNumber === act.activityNumber)
            if (match && !match.taskNumber && task.taskNumber) {
              match.taskNumber = task.taskNumber
            }
          }
        }
      })
    })
  }

  const scanAppointments = apptsList => {
    if (!Array.isArray(apptsList)) return
    apptsList.forEach(appt => {
      if (appt?.onReportNumber) return
      const activities = Array.isArray(appt?.activities) ? appt.activities : []
      activities.forEach(act => {
        if (act?.onReportNumber) return
        if (isReportPrepActivity(act)) {
          const alreadyFound = candidates.some(
            c => c.activityNumber != null && act.activityNumber != null && c.activityNumber === act.activityNumber,
          )
          if (!alreadyFound) {
            candidates.push({
              activity: act,
              activityNumber: act.activityNumber,
              taskNumber: null,
              appointmentNumber: appt.appointmentNumber || act.appointmentNumber || null,
            })
          } else {
            const match = candidates.find(c => c.activityNumber === act.activityNumber)
            if (match && !match.appointmentNumber && appt.appointmentNumber) {
              match.appointmentNumber = appt.appointmentNumber
            }
          }
        }
      })
    })
  }

  scanTasks(reportModel?.tasks)
  scanAppointments(reportModel?.appointments)
  scanTasks(fallbackTasks)
  scanAppointments(fallbackAppointments)

  if (candidates.length === 0) return null
  return candidates[candidates.length - 1]
}

const buildCaseMemberOptions = (caseMembers = [], user = null, existingActivity = null) => {
  const options = []
  if (Array.isArray(caseMembers)) {
    caseMembers.forEach(cm => {
      const id = cm?.id || cm?.userId
      if (!id) return
      if (options.some(o => String(o.id).toLowerCase() === String(id).toLowerCase())) return
      const fullName = `${cm.firstName || ''} ${cm.lastName || ''}`.trim() || cm.name || cm.username || String(id)
      options.push({
        id,
        name: cm.role ? `${fullName} (${cm.role})` : fullName,
      })
    })
  }

  if (user?.id && !options.some(o => String(o.id).toLowerCase() === String(user.id).toLowerCase())) {
    const userFullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Current User'
    options.unshift({
      id: user.id,
      name: `${userFullName} (You)`,
    })
  }

  const existingUserId = existingActivity?.userId || existingActivity?.user?.id
  if (existingUserId && !options.some(o => String(o.id).toLowerCase() === String(existingUserId).toLowerCase())) {
    const existingName =
      existingActivity?.userName ||
      `${existingActivity?.user?.firstName || ''} ${existingActivity?.user?.lastName || ''}`.trim() ||
      'Assigned Member'
    options.push({
      id: existingUserId,
      name: existingName,
    })
  }

  return options
}

const ReportPrepBillablePrompt = forwardRef(
  (
    {
      caseNumber,
      reportModel: propReportModel,
      caseMembers: propCaseMembers,
      user,
      fallbackTasks = [],
      fallbackAppointments = [],
      defaultDescription = 'Report preparation',
      onActivityFormVisibleChange,
    },
    ref,
  ) => {
    const [fetchedReportModel, setFetchedReportModel] = useState(null)
    const effectiveReportModel = propReportModel || fetchedReportModel

    const effectiveCaseMembers = useMemo(() => {
      if (Array.isArray(propCaseMembers) && propCaseMembers.length > 0) return propCaseMembers
      if (Array.isArray(effectiveReportModel?.case?.caseMembers)) return effectiveReportModel.case.caseMembers
      return []
    }, [propCaseMembers, effectiveReportModel])

    const existingMatch = useMemo(
      () => findExistingReportPrepActivity(effectiveReportModel, fallbackTasks, fallbackAppointments),
      [effectiveReportModel, fallbackTasks, fallbackAppointments],
    )

    const caseMemberOptions = useMemo(
      () => buildCaseMemberOptions(effectiveCaseMembers, user, existingMatch?.activity),
      [effectiveCaseMembers, user, existingMatch],
    )

    const defaultUserId = useMemo(() => {
      if (user?.id) {
        const matchInOptions = caseMemberOptions.find(o => String(o.id).toLowerCase() === String(user.id).toLowerCase())
        if (matchInOptions) return matchInOptions.id
        return user.id
      }
      return caseMemberOptions[0]?.id || ''
    }, [user, caseMemberOptions])

    const buildNormalizedExisting = match => {
      if (!match?.activity) return null
      const act = match.activity
      const rawUserId = act.userId || act.user?.id || defaultUserId
      const matchedOpt = caseMemberOptions.find(o => String(o.id).toLowerCase() === String(rawUserId).toLowerCase())
      return {
        activityNumber: act.activityNumber || 0,
        date: normalizeActivityDate(act.date),
        type: act.type || 'Report',
        time: act.time != null ? parseFloat(act.time) : 1,
        description: act.description || defaultDescription,
        userId: matchedOpt ? matchedOpt.id : rawUserId,
      }
    }

    const [existingMeta, setExistingMeta] = useState(() => {
      if (!existingMatch) return null
      const normalized = buildNormalizedExisting(existingMatch)
      return {
        ...existingMatch,
        normalizedOriginal: normalized,
      }
    })

    const [formModel, setFormModel] = useState(() => {
      if (existingMatch) {
        return { activities: [buildNormalizedExisting(existingMatch)] }
      }
      return { activities: [] }
    })

    const userInteractedRef = useRef(false)

    // Fetch in-progress report if not provided via props (e.g. from OverviewPage Confirm Close Case modal)
    useEffect(() => {
      let isMounted = true
      if (!propReportModel && caseNumber && typeof CasesService.getInProgressReport === 'function') {
        Promise.resolve(CasesService.getInProgressReport(caseNumber))
          .then(res => {
            if (isMounted && res) {
              setFetchedReportModel(res)
            }
          })
          .catch(() => {
            // Ignore fetch errors in fallback mode
          })
      }
      return () => {
        isMounted = false
      }
    }, [propReportModel, caseNumber])

    // Sync if existingMatch arrives after async getInProgressReport fetch and user hasn't interacted yet
    useEffect(() => {
      if (existingMatch && !userInteractedRef.current && !existingMeta) {
        const normalized = buildNormalizedExisting(existingMatch)
        setExistingMeta({
          ...existingMatch,
          normalizedOriginal: normalized,
        })
        setFormModel({ activities: [normalized] })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingMatch])

    const hasActivityForm = Array.isArray(formModel.activities) && formModel.activities.length > 0

    useEffect(() => {
      if (typeof onActivityFormVisibleChange === 'function') {
        onActivityFormVisibleChange(hasActivityForm)
      }
    }, [hasActivityForm, onActivityFormVisibleChange])

    const [touched, setTouched] = useState({})
    const validationSchema = useMemo(
      () =>
        validation.object({
          activities: validation.arrayOf(validation.activity('Activity', true)),
        }),
      [],
    )

    const { createInputProps, ErrorMessage } = createFormHelpers({
      touchedObject: touched,
      setTouchedFunction: setTouched,
      submitAttempted: false,
      validationSchema,
    })

    const handleSetModel = nextModel => {
      userInteractedRef.current = true
      setFormModel(nextModel)
    }

    const handleQuickAddReportPrep = () => {
      userInteractedRef.current = true
      setFormModel({
        activities: [
          {
            activityNumber: existingMeta?.activityNumber || 0,
            date: Moment().format('YYYY-MM-DD'),
            type: 'Report',
            time: 1,
            description: defaultDescription,
            userId: defaultUserId,
          },
        ],
      })
    }

    const handleRemoveActivity = () => {
      userInteractedRef.current = true
      setFormModel({ activities: [] })
    }

    const persistExistingActivityChange = async (meta, updatedActivity, isDelete) => {
      const actNum = meta.activityNumber

      // Resolve parent taskNumber or appointmentNumber (never use the windowed reportModel copy as the PUT base)
      const matchedReportOrFallbackTask =
        (effectiveReportModel?.tasks || []).find(
          t =>
            (meta.taskNumber && t.taskNumber === meta.taskNumber) ||
            (actNum != null && (t.activities || []).some(a => a.activityNumber === actNum)),
        ) ||
        (fallbackTasks || []).find(
          t =>
            (meta.taskNumber && t.taskNumber === meta.taskNumber) ||
            (actNum != null && (t.activities || []).some(a => a.activityNumber === actNum)),
        )
      const taskNum =
        meta.taskNumber ||
        effectiveReportModel?.activityToTaskLookup?.[actNum] ||
        matchedReportOrFallbackTask?.taskNumber

      const matchedReportOrFallbackAppt =
        (effectiveReportModel?.appointments || []).find(
          a =>
            (meta.appointmentNumber && a.appointmentNumber === meta.appointmentNumber) ||
            (actNum != null && (a.activities || []).some(act => act.activityNumber === actNum)),
        ) ||
        (fallbackAppointments || []).find(
          a =>
            (meta.appointmentNumber && a.appointmentNumber === meta.appointmentNumber) ||
            (actNum != null && (a.activities || []).some(act => act.activityNumber === actNum)),
        )
      const apptNum =
        meta.appointmentNumber ||
        effectiveReportModel?.activityToApptLookup?.[actNum] ||
        matchedReportOrFallbackAppt?.appointmentNumber

      const formatIsoDate = rawDate => {
        const cleanDate = normalizeActivityDate(rawDate)
        return `${cleanDate}T12:00:00.000Z`
      }

      // 1. Always fetch the full Task record from the API before building the PUT payload
      let fullTask = null
      if (taskNum && typeof TasksService.get === 'function') {
        fullTask = await TasksService.get(caseNumber, taskNum)
      }
      if (!fullTask && actNum != null && typeof TasksService.getAllForCase === 'function') {
        const allTasks = await TasksService.getAllForCase(caseNumber)
        fullTask = (allTasks || []).find(
          t => (taskNum && t.taskNumber === taskNum) || (t.activities || []).some(a => a.activityNumber === actNum),
        )
      }

      if (fullTask && typeof TasksService.update === 'function') {
        const updatedActivities = isDelete
          ? (fullTask.activities || []).filter(a => a.activityNumber !== actNum)
          : (fullTask.activities || []).map(a =>
              a.activityNumber === actNum
                ? {
                    ...a,
                    date: formatIsoDate(updatedActivity.date),
                    type: updatedActivity.type || 'Report',
                    time: Math.max(0.1, Math.round((parseFloat(updatedActivity.time) || 1) * 10) / 10),
                    description: (updatedActivity.description || defaultDescription).trim(),
                    userId: updatedActivity.userId || a.userId,
                  }
                : a,
            )
        await TasksService.update(
          {
            ...fullTask,
            activities: updatedActivities,
          },
          caseNumber,
          fullTask.taskNumber || taskNum,
        )
        return
      }

      // 2. Always fetch the full Appointment record from the API before building the PUT payload
      let fullAppt = null
      if (apptNum && typeof AppointmentsService.get === 'function') {
        fullAppt = await AppointmentsService.get(caseNumber, apptNum)
      }
      if (!fullAppt && actNum != null && typeof AppointmentsService.getAllForCase === 'function') {
        const allAppts = await AppointmentsService.getAllForCase(caseNumber)
        fullAppt = (allAppts || []).find(
          a =>
            (apptNum && a.appointmentNumber === apptNum) ||
            (a.activities || []).some(act => act.activityNumber === actNum),
        )
      }

      if (fullAppt && typeof AppointmentsService.update === 'function') {
        const updatedActivities = isDelete
          ? (fullAppt.activities || []).filter(a => a.activityNumber !== actNum)
          : (fullAppt.activities || []).map(a =>
              a.activityNumber === actNum
                ? {
                    ...a,
                    date: formatIsoDate(updatedActivity.date),
                    type: updatedActivity.type || 'Report',
                    time: Math.max(0.1, Math.round((parseFloat(updatedActivity.time) || 1) * 10) / 10),
                    description: (updatedActivity.description || defaultDescription).trim(),
                    userId: updatedActivity.userId || a.userId,
                  }
                : a,
            )
        await AppointmentsService.update(caseNumber, fullAppt.appointmentNumber || apptNum, {
          ...fullAppt,
          activities: updatedActivities,
        })
        return
      }

      throw new Error('Unable to load the parent task or appointment to update the Report Preparation activity.')
    }

    const persistIfNeeded = async () => {
      const currentActivity = formModel.activities?.[0] || null

      if (existingMeta) {
        if (!currentActivity) {
          await persistExistingActivityChange(existingMeta, null, true)
          return { action: 'deleted' }
        }

        const orig = existingMeta.normalizedOriginal || {}
        const hasChanged =
          normalizeActivityDate(currentActivity.date) !== normalizeActivityDate(orig.date) ||
          String(currentActivity.type || '') !== String(orig.type || '') ||
          Number(currentActivity.time) !== Number(orig.time) ||
          String(currentActivity.description || '').trim() !== String(orig.description || '').trim() ||
          String(currentActivity.userId || '') !== String(orig.userId || '')

        if (hasChanged) {
          await persistExistingActivityChange(existingMeta, currentActivity, false)
          return { action: 'updated', activity: currentActivity }
        }

        return { action: 'accepted', activity: currentActivity }
      }

      if (currentActivity && parseFloat(currentActivity.time) > 0) {
        await CasesService.logBillableActivity(caseNumber, {
          date: normalizeActivityDate(currentActivity.date),
          type: currentActivity.type || 'Report',
          time: parseFloat(currentActivity.time) || 1,
          description: (currentActivity.description || defaultDescription).trim(),
          userId: currentActivity.userId || defaultUserId || undefined,
        })
        return { action: 'created', activity: currentActivity }
      }

      return { action: 'none' }
    }

    useImperativeHandle(ref, () => ({
      persistIfNeeded,
      hasActivityForm,
    }))

    return (
      <div className="mt-3 pt-2 border-top" data-testid="report-prep-billable-section">
        {!hasActivityForm ? (
          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: '6px' }}
            data-testid="report-prep-quick-log"
          >
            <span className="text-muted small mr-1">
              <i className="fas fa-stopwatch text-primary mr-1" />
              Quick Log:
            </span>
            <button
              type="button"
              data-testid="report-prep-quick-log-btn"
              className="btn btn-sm btn-outline-primary py-0 px-2"
              style={{ fontSize: '0.75rem', borderRadius: '12px' }}
              onClick={handleQuickAddReportPrep}
            >
              + Report Preparation (1.0h)
            </button>
          </div>
        ) : (
          <div data-testid="report-prep-activity-form">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small font-weight-bold text-secondary">
                <i className="fas fa-stopwatch text-primary mr-1" />
                {existingMeta ? 'Report Preparation Activity (Accept or Edit)' : 'Report Preparation Activity'}
              </span>
            </div>
            <div className="form-row hide-sm-down" style={{ paddingLeft: '11px', paddingRight: '11px' }}>
              <div className="form-label col-md-2">Date</div>
              <div className="form-label col-md-2">Type of Activity</div>
              <div className="form-label col-md-1">Time</div>
              <div className="form-label col-md-4">Description</div>
              <div className="form-label col-md-2">Case Member</div>
              <div className="form-label col-md-1"></div>
            </div>
            <Activity
              namePrefix="activities[0]"
              model={formModel}
              setModelFunction={handleSetModel}
              createInputProps={createInputProps}
              ErrorMessage={ErrorMessage}
              handleRemove={handleRemoveActivity}
              allowEditing={true}
              caseMemberOptions={caseMemberOptions}
            />
          </div>
        )}
      </div>
    )
  },
)

ReportPrepBillablePrompt.displayName = 'ReportPrepBillablePrompt'

export default ReportPrepBillablePrompt
